import * as React from "react";
import { 
  ArrowLeft, HelpCircle, BarChart3, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle2, XCircle, Calculator, Target,
  Lightbulb, BookOpen, ChevronRight, Info
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SemaphoreExample = ({ level, color, bgColor, description, zRange, example }) => (
  <div className={`p-4 rounded-lg border-l-4 ${color} ${bgColor}`}>
    <div className="flex items-start gap-3">
      <div className="mt-0.5">
        {level === "CRITICAL" && <XCircle className="h-5 w-5 text-red-500" />}
        {level === "WARNING" && <AlertTriangle className="h-5 w-5 text-amber-500" />}
        {level === "NORMAL" && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold">{level}</span>
          <Badge variant="outline" className="text-xs">{zRange}</Badge>
        </div>
        <p className="text-sm text-slate-600 mb-2">{description}</p>
        <div className="text-xs bg-white/50 p-2 rounded border">
          <span className="font-medium">Ejemplo:</span> {example}
        </div>
      </div>
    </div>
  </div>
);

const MetricExplanation = ({ name, icon: Icon, description, normalRange, whatItMeans }) => (
  <div className="p-4 bg-slate-50 rounded-lg">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4 text-purple-500" />
      <span className="font-semibold text-slate-800">{name}</span>
    </div>
    <p className="text-sm text-slate-600 mb-2">{description}</p>
    <div className="space-y-1 text-xs">
      <p><span className="font-medium text-slate-700">Rango normal:</span> {normalRange}</p>
      <p><span className="font-medium text-slate-700">¿Qué significa una anomalía?</span> {whatItMeans}</p>
    </div>
  </div>
);

export const ZScoreInfoPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-[#e8eef3]">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al Análisis
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800">Guía de Análisis Z-Score</h1>
                <p className="text-xs text-slate-500">Entendiendo la detección de anomalías</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Introduction */}
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="p-3 rounded-full bg-purple-100 h-fit">
                <Lightbulb className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">¿Qué es el Análisis Z-Score?</h2>
                <p className="text-slate-600 leading-relaxed">
                  El <strong>Z-Score</strong> (o puntuación estándar) es una medida estadística que indica 
                  <strong> cuántas desviaciones estándar</strong> se encuentra un valor respecto a la media histórica. 
                  Es una herramienta poderosa para identificar <strong>comportamientos inusuales</strong> en tus métricas 
                  de marketing digital sin necesidad de definir umbrales fijos.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How it works */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-purple-500" />
              ¿Cómo funciona?
            </CardTitle>
            <CardDescription>
              El proceso de detección de anomalías paso a paso
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg border-t-4 border-purple-400">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-bold">1</div>
                  <span className="font-semibold">Recolección</span>
                </div>
                <p className="text-sm text-slate-600">
                  Se obtienen las métricas diarias de las cuentas de Google Ads del cliente seleccionado: 
                  impresiones, clics, conversiones, costos, CTR y tasa de conversión.
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border-t-4 border-pink-400">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-pink-500 text-white flex items-center justify-center text-sm font-bold">2</div>
                  <span className="font-semibold">Cálculo Estadístico</span>
                </div>
                <p className="text-sm text-slate-600">
                  Para cada cuenta, se calcula la <strong>media</strong> y <strong>desviación estándar</strong> 
                  histórica de cada métrica, creando una "línea base" de comportamiento normal.
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border-t-4 border-indigo-400">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-sm font-bold">3</div>
                  <span className="font-semibold">Detección</span>
                </div>
                <p className="text-sm text-slate-600">
                  El Z-Score se calcula: <code className="bg-white px-1 rounded">(valor - media) / desviación</code>. 
                  Valores alejados de 0 indican comportamiento inusual.
                </p>
              </div>
            </div>

            {/* Formula visualization */}
            <div className="p-4 bg-gradient-to-r from-slate-800 to-slate-900 rounded-lg text-white">
              <p className="text-xs text-slate-400 mb-2">FÓRMULA DEL Z-SCORE</p>
              <div className="text-center py-4">
                <span className="text-2xl font-mono">
                  Z = (X - μ) / σ
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm mt-4">
                <div className="text-center">
                  <span className="text-purple-400 font-bold">X</span>
                  <p className="text-xs text-slate-400">Valor actual</p>
                </div>
                <div className="text-center">
                  <span className="text-pink-400 font-bold">μ</span>
                  <p className="text-xs text-slate-400">Media histórica</p>
                </div>
                <div className="text-center">
                  <span className="text-indigo-400 font-bold">σ</span>
                  <p className="text-xs text-slate-400">Desviación estándar</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Traffic Light System */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-500" />
              Sistema de Semáforos
            </CardTitle>
            <CardDescription>
              Cómo interpretar los niveles de alerta basados en el Z-Score
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <SemaphoreExample
              level="CRITICAL"
              color="border-red-500"
              bgColor="bg-red-50"
              zRange="|Z| > 3"
              description="El valor está a más de 3 desviaciones estándar de la media. Esto ocurre solo en el 0.3% de los casos en una distribución normal. Requiere atención inmediata."
              example="Si normalmente tienes 1,000 clics diarios con una desviación de 200, un día con 400 clics (Z = -3) o 1,600 clics (Z = +3) activaría una alerta crítica."
            />
            <SemaphoreExample
              level="WARNING"
              color="border-amber-500"
              bgColor="bg-amber-50"
              zRange="2 < |Z| ≤ 3"
              description="El valor está entre 2 y 3 desviaciones estándar. Ocurre aproximadamente en el 4.5% de los casos. Vale la pena investigar."
              example="Con los mismos 1,000 clics y desviación de 200, un día con 550 clics (Z = -2.25) o 1,450 clics (Z = +2.25) generaría una advertencia."
            />
            <SemaphoreExample
              level="NORMAL"
              color="border-emerald-500"
              bgColor="bg-emerald-50"
              zRange="|Z| ≤ 2"
              description="El valor está dentro de 2 desviaciones estándar de la media. Representa el 95% del comportamiento normal esperado."
              example="Valores entre 600 y 1,400 clics se considerarían normales y no activarían ninguna alerta."
            />

            {/* Visual scale */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-3">Escala Visual del Z-Score</p>
              <div className="relative h-8 bg-gradient-to-r from-red-500 via-amber-400 via-emerald-400 via-amber-400 to-red-500 rounded-full">
                <div className="absolute inset-0 flex items-center justify-between px-2 text-xs font-bold text-white">
                  <span>-3</span>
                  <span>-2</span>
                  <span>0</span>
                  <span>+2</span>
                  <span>+3</span>
                </div>
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>🔴 Crítico bajo</span>
                <span>🟡 Advertencia</span>
                <span>🟢 Normal</span>
                <span>🟡 Advertencia</span>
                <span>🔴 Crítico alto</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Metrics Explained */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-500" />
              Métricas Monitoreadas
            </CardTitle>
            <CardDescription>
              Qué significa cada métrica y cómo interpretar sus anomalías
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <MetricExplanation
                name="Impressions (Impresiones)"
                icon={TrendingUp}
                description="Número de veces que tus anuncios se mostraron a usuarios."
                normalRange="Varía según presupuesto y segmentación"
                whatItMeans="Una caída drástica puede indicar problemas de presupuesto, pausa de campañas o cambios en la competencia. Un aumento inusual podría señalar viralidad o errores en segmentación."
              />
              <MetricExplanation
                name="Clicks"
                icon={Target}
                description="Número de clics en tus anuncios."
                normalRange="Depende de impresiones y CTR histórico"
                whatItMeans="Cambios abruptos pueden indicar problemas con los anuncios, landing pages o cambios en el comportamiento del usuario."
              />
              <MetricExplanation
                name="Conversions (Conversiones)"
                icon={CheckCircle2}
                description="Acciones valiosas completadas (compras, registros, etc.)."
                normalRange="Basado en tu tasa de conversión histórica"
                whatItMeans="Anomalías pueden indicar problemas técnicos en el sitio, cambios en la oferta o estacionalidad no prevista."
              />
              <MetricExplanation
                name="Cost (Costo en USD)"
                icon={TrendingDown}
                description="Gasto total en publicidad."
                normalRange="Según presupuesto diario configurado"
                whatItMeans="Un gasto inusualmente alto puede indicar CPCs elevados o pacing agresivo. Un gasto bajo puede señalar pausas o problemas de entrega."
              />
              <MetricExplanation
                name="CTR (Click-Through Rate)"
                icon={BarChart3}
                description="Porcentaje de impresiones que generaron clic."
                normalRange="Típicamente 1-5% según industria"
                whatItMeans="Un CTR bajo puede indicar fatiga de anuncios, mala segmentación o creativos poco atractivos. Un CTR muy alto podría ser clickbait o audiencias muy específicas."
              />
              <MetricExplanation
                name="Conversion Rate (Tasa de Conversión)"
                icon={Target}
                description="Porcentaje de clics que generaron conversiones."
                normalRange="Varía mucho según industria y objetivo"
                whatItMeans="Cambios drásticos a menudo señalan problemas en el funnel de conversión, landing pages o tracking."
              />
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-purple-500" />
              Preguntas Frecuentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>¿Por qué usar Z-Score en lugar de umbrales fijos?</AccordionTrigger>
                <AccordionContent>
                  <p className="text-slate-600">
                    Los umbrales fijos (ej: "alertar si clics &lt; 100") no consideran la variabilidad natural de cada cuenta. 
                    Una cuenta que normalmente tiene 50 clics no debería evaluarse igual que una con 5,000. 
                    El Z-Score se adapta automáticamente al comportamiento histórico de cada cuenta, 
                    haciendo las alertas más precisas y reduciendo falsos positivos.
                  </p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger>¿Qué hago cuando veo una alerta CRITICAL?</AccordionTrigger>
                <AccordionContent>
                  <div className="text-slate-600 space-y-2">
                    <p><strong>1. Verifica el contexto:</strong> ¿Hay algún evento conocido (festivo, promoción, problema técnico)?</p>
                    <p><strong>2. Revisa la métrica específica:</strong> Identifica cuál métrica está fuera de rango y en qué dirección.</p>
                    <p><strong>3. Investiga en la plataforma:</strong> Accede a Google Ads para ver detalles de campañas afectadas.</p>
                    <p><strong>4. Documenta y actúa:</strong> Si es un problema real, toma acción correctiva. Si es esperado, documéntalo.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger>¿El sistema detecta tanto subidas como bajadas?</AccordionTrigger>
                <AccordionContent>
                  <p className="text-slate-600">
                    Sí. El Z-Score puede ser positivo (valor por encima de la media) o negativo (por debajo). 
                    Ambos extremos activan alertas porque cualquier desviación significativa merece atención. 
                    Un aumento inusual en costos es tan importante como una caída inesperada en conversiones.
                  </p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4">
                <AccordionTrigger>¿Cuántos datos históricos se usan para el cálculo?</AccordionTrigger>
                <AccordionContent>
                  <p className="text-slate-600">
                    El sistema utiliza todos los datos históricos disponibles para cada cuenta. 
                    Esto permite calcular una media y desviación estándar más precisas. 
                    Las cuentas con más historial tendrán cálculos más estables y confiables.
                  </p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5">
                <AccordionTrigger>¿Por qué una métrica puede mostrar Z-Score de 0?</AccordionTrigger>
                <AccordionContent>
                  <p className="text-slate-600">
                    Un Z-Score de 0 significa que el valor actual es exactamente igual a la media histórica. 
                    También puede aparecer cuando la desviación estándar es 0 (todos los valores históricos son iguales) 
                    o cuando no hay suficientes datos para calcular estadísticas significativas.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-purple-500" />
              Consejos para Aprovechar las Alertas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex gap-2">
                <ChevronRight className="h-5 w-5 text-purple-400 flex-shrink-0" />
                <span className="text-slate-600">
                  <strong>Configura alertas programadas</strong> para recibir notificaciones automáticas sin necesidad de revisar manualmente.
                </span>
              </li>
              <li className="flex gap-2">
                <ChevronRight className="h-5 w-5 text-purple-400 flex-shrink-0" />
                <span className="text-slate-600">
                  <strong>Activa alertas WARNING</strong> si prefieres un monitoreo más proactivo y detectar tendencias antes de que se vuelvan críticas.
                </span>
              </li>
              <li className="flex gap-2">
                <ChevronRight className="h-5 w-5 text-purple-400 flex-shrink-0" />
                <span className="text-slate-600">
                  <strong>Revisa múltiples métricas juntas:</strong> Si varias métricas están anómalas simultáneamente, es más probable que haya un problema real.
                </span>
              </li>
              <li className="flex gap-2">
                <ChevronRight className="h-5 w-5 text-purple-400 flex-shrink-0" />
                <span className="text-slate-600">
                  <strong>Usa el tooltip</strong> en cada celda de la tabla para ver el Z-Score exacto y entender la magnitud de la anomalía.
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Back button */}
        <div className="text-center pt-4">
          <Button onClick={onBack} className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al Análisis de Anomalías
          </Button>
        </div>
      </div>
    </div>
  );
};
